import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { createSearchParams } from 'react-router-dom';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { BELOW_DESKTOP_QUERY } from '@/config/ui.constants';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { DateRangeValue } from '../date-range-filter/DateRangeFilter';
import PropertyAdvancedFilters from './PropertyAdvancedFilters';
import OrderAdvancedFilters from './OrderAdvancedFilters';
import type { OrderAdvancedFiltersProps } from './OrderAdvancedFilters';
import {
  createPropertyParams,
  createOrderParams,
  countOrderFilters,
  SelectOption,
} from '../utils/advancedFilterUtils';
import { TableFilter } from '../metadata/propertyConfig';
import {
  ACTIVE_STATUS_ACTIVE,
  ACTIVE_STATUS_ALL,
  ACTIVE_STATUS_INACTIVE,
  TRISTATE_EITHER,
} from '../more-filters/metadata/FilterOption';

export type ActiveStatusOption = { value: string; label: string };

const ACTIVE_UNITS_OPTION: ActiveStatusOption = {
  value: 'Active Units',
  label: 'Active Units',
};
const INACTIVE_UNITS_OPTION: ActiveStatusOption = {
  value: 'Inactive Units',
  label: 'Inactive Units',
};

const activeStatusToRadio = (
  option: ActiveStatusOption | null | undefined
): string => {
  if (option?.value === 'Active Units') return ACTIVE_STATUS_ACTIVE;
  if (option?.value === 'Inactive Units') return ACTIVE_STATUS_INACTIVE;
  return ACTIVE_STATUS_ALL;
};

const radioToActiveStatus = (
  value: string
): ActiveStatusOption | null => {
  if (value === ACTIVE_STATUS_ACTIVE) return ACTIVE_UNITS_OPTION;
  if (value === ACTIVE_STATUS_INACTIVE) return INACTIVE_UNITS_OPTION;
  return null;
};

export interface MoreFiltersDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isProperty: boolean;
  filter: string;
  activeStatus: ActiveStatusOption | null | undefined;
  onApplyFilters: (
    filterString: string,
    activeStatusValue: ActiveStatusOption | null
  ) => void;
  onResetFilters: () => void;
  setFiltersCount: (count: number) => void;
  /**
   * Staged quick-filter controls (Order Status, Order Date, Entity, or
   * property Unit Status — whichever the page's inline row normally shows).
   * Rendered as the first section of the scrollable body, below `lg` only;
   * the page owns the staged state, this component only hosts the slot.
   */
  quickFilters?: ReactNode;
  /**
   * Commits the page's staged quick-filter state together with the advanced
   * filter this Apply is also submitting. Takes the SAME (filterString,
   * activeStatusValue) `applyFilters` would otherwise pass to `onApplyFilters`
   * — when this prop is present, `applyFilters` calls it INSTEAD of
   * `onApplyFilters` (not in addition), so there is exactly one appendFilter
   * refetch per Apply. Two independent calls here — one for advanced, one for
   * quick — would each read the other's pre-update filter/moreFilters
   * snapshot (this-render closures) and could silently discard whichever
   * change fired first from the network request.
   */
  onApplyQuickFilters?: (
    filterString: string,
    activeStatusValue: ActiveStatusOption | null
  ) => void;
  /**
   * Clears quick AND advanced filters in one commit. Below `lg` the sheet is
   * the only filters surface (quick + advanced), so its Reset must cover both
   * populations — `resetFilters` calls this INSTEAD of `onResetFilters` there
   * (one refetch, mirroring `onApplyQuickFilters`'s instead-of contract).
   * At `lg`+ the sheet shows advanced filters only and Reset stays scoped to
   * them via `onResetFilters` — the quick controls live in the inline row
   * with their own per-chip removal, and widening desktop Reset would be a
   * behavior change this prop deliberately does not make.
   */
  onResetAllFilters?: () => void;
  /** Rendered at the top of the scrollable body — e.g. the identifier-search
   *  window annotation (DV-2848, spec §5). */
  notice?: ReactNode;
  /** Passed through to OrderAdvancedFilters.injectedOptions. */
  orderOptions?: OrderAdvancedFiltersProps['injectedOptions'];
}

const MoreFiltersDrawer = ({
  open,
  onOpenChange,
  isProperty,
  filter,
  activeStatus,
  onApplyFilters,
  onResetFilters,
  setFiltersCount,
  quickFilters,
  onApplyQuickFilters,
  onResetAllFilters,
  notice,
  orderOptions,
}: MoreFiltersDrawerProps) => {
  const isMobile = useMediaQuery(BELOW_DESKTOP_QUERY);

  // --- Property state ---
  const [photosFilter, setPhotosFilter] = useState(TRISTATE_EITHER);
  const [tourFilter, setTourFilter] = useState(TRISTATE_EITHER);
  const [lastOrderDate, setLastOrderDate] = useState('');
  const [occupancyStatus, setOccupancyStatus] = useState('');
  const [portfolio, setPortfolio] = useState('');
  const [leaseStatus, setLeaseStatus] = useState('');
  const [activeStatusFilter, setActiveStatusFilter] = useState<string>(() =>
    activeStatusToRadio(activeStatus)
  );

  // Properties Displayed lives outside moreFilters; reflect external changes
  // (Clear Filters, fresh deep-link entry) back into the radio.
  useEffect(() => {
    setActiveStatusFilter(activeStatusToRadio(activeStatus));
  }, [activeStatus]);

  // --- Order state ---
  const [address, setAddress] = useState('');
  const [selectedServices, setSelectedServices] = useState<SelectOption[]>([]);
  const [selectedOrderedBy, setSelectedOrderedBy] = useState<SelectOption[]>([]);
  const [appointmentDateRange, setAppointmentDateRange] =
    useState<DateRangeValue | null>(null);
  const [orderCompletedDateRange, setOrderCompletedDateRange] =
    useState<DateRangeValue | null>(null);
  const [accessType, setAccessType] = useState('');
  const [unableToComplete, setUnableToComplete] = useState('');

  const isPropertyDirty = (): boolean => {
    return (
      photosFilter !== TRISTATE_EITHER ||
      tourFilter !== TRISTATE_EITHER ||
      lastOrderDate !== '' ||
      activeStatusFilter !== ACTIVE_STATUS_ACTIVE ||
      !!leaseStatus ||
      !!portfolio ||
      !!occupancyStatus
    );
  };

  const isDisabled = (): boolean => {
    if (isProperty) return !isPropertyDirty();
    return (
      selectedServices.length === 0 &&
      address === '' &&
      selectedOrderedBy.length === 0 &&
      appointmentDateRange === null &&
      orderCompletedDateRange === null &&
      unableToComplete === '' &&
      !accessType
    );
  };

  // Tally the badge: each non-default property filter counts as 1.
  // Active (default) doesn't count; Inactive/All do.
  const countPropertyFilters = (
    params: Record<string, string>,
    activeRadio: string
  ): number => {
    return (
      Object.keys(params).length +
      (activeRadio !== ACTIVE_STATUS_ACTIVE ? 1 : 0)
    );
  };

  const applyFilters = () => {
    if (isProperty) {
      const params = createPropertyParams({
        occupancyStatus,
        leaseStatus,
        portfolio,
        photosFilter,
        tourFilter,
        lastOrderDate,
      });
      setFiltersCount(countPropertyFilters(params, activeStatusFilter));
      const mf = createSearchParams(params).toString();
      const nextActiveStatus = radioToActiveStatus(activeStatusFilter);
      if (onApplyQuickFilters) onApplyQuickFilters(mf, nextActiveStatus);
      else onApplyFilters(mf, nextActiveStatus);
    } else {
      const params = createOrderParams({
        address,
        unableToComplete,
        accessType,
        appointmentDateRange,
        orderCompletedDateRange,
        services: selectedServices,
        orderedBy: selectedOrderedBy,
      });
      setFiltersCount(countOrderFilters(params));
      const mf = createSearchParams(params as Record<string, string>).toString();
      if (onApplyQuickFilters) onApplyQuickFilters(mf, null);
      else onApplyFilters(mf, null);
    }
    onOpenChange(false);
  };

  const resetFilters = () => {
    if (isProperty) {
      setPhotosFilter(TRISTATE_EITHER);
      setTourFilter(TRISTATE_EITHER);
      setLastOrderDate('');
      setOccupancyStatus('');
      setLeaseStatus('');
      setPortfolio('');
      setActiveStatusFilter(ACTIVE_STATUS_ACTIVE);
    } else {
      setAddress('');
      setSelectedServices([]);
      setSelectedOrderedBy([]);
      setAppointmentDateRange(null);
      setOrderCompletedDateRange(null);
      setUnableToComplete('');
      setAccessType('');
    }
    // Below lg the sheet displays quick + advanced filters, so Reset clears
    // both (the page's onResetAllFilters — one setFilters batch, one
    // refetch). At lg+ the sheet is advanced-only and keeps its
    // advanced-scoped reset.
    if (isMobile && quickFilters && onResetAllFilters) onResetAllFilters();
    else onResetFilters();
    setFiltersCount(0);
    onOpenChange(false);
  };

  const body = (
    <div className="flex-1 overflow-y-auto px-6 py-6">
      {notice && (
        <div className="mb-4 rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
          {notice}
        </div>
      )}
      {isMobile && quickFilters && (
        <div className="flex flex-col gap-4 border-b border-border pb-6 mb-6">
          {quickFilters}
        </div>
      )}
      {isProperty ? (
        <PropertyAdvancedFilters
          filter={filter}
          onPhotosChange={setPhotosFilter}
          onTourChange={setTourFilter}
          onLastOrderDateChange={setLastOrderDate}
          onOccupancyStatusChange={setOccupancyStatus}
          onLeaseStatusChange={setLeaseStatus}
          onPortfolioChange={setPortfolio}
          onActiveStatusChange={setActiveStatusFilter}
          photosFilter={photosFilter}
          tourFilter={tourFilter}
          lastOrderDate={lastOrderDate}
          activeStatusFilter={activeStatusFilter}
        />
      ) : (
        <OrderAdvancedFilters
          filter={filter}
          selectedServices={selectedServices}
          selectedOrderedBy={selectedOrderedBy}
          appointmentDateRange={appointmentDateRange}
          orderCompletedDateRange={orderCompletedDateRange}
          accessType={accessType}
          unableToComplete={unableToComplete}
          onAddressChange={setAddress}
          onServicesChange={setSelectedServices}
          onOrderedByChange={setSelectedOrderedBy}
          onAppointmentDateChange={setAppointmentDateRange}
          onOrderCompletedDateChange={setOrderCompletedDateRange}
          onAccessTypeChange={setAccessType}
          onUnableToCompleteChange={setUnableToComplete}
          injectedOptions={orderOptions}
        />
      )}
    </div>
  );

  // isDisabled() only tracks this component's own advanced-filter state; the
  // quickFilters slot's dirtiness lives in the page's staged state, which
  // this component can't see. Below lg the sheet is the *only* filters
  // surface (quick + advanced), so gating Apply or Reset on advanced-only
  // dirtiness would strand quick-filter-only state with no way to commit or
  // clear it — keep both enabled there (Reset on already-default filters is
  // a harmless no-op refetch, same trade Apply already makes).
  const isMobileWithQuickFilters = isMobile && !!quickFilters;
  const isApplyDisabled = isMobileWithQuickFilters ? false : isDisabled();
  const isResetDisabled = isMobileWithQuickFilters ? false : isDisabled();

  const footer = (
    <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border bg-card px-6 py-4">
      <Button
        type="button"
        variant="link"
        // Standalone action, not an inline prose link — carries the 44px
        // floor the link variant itself deliberately omits (WCAG 2.5.8
        // inline exception; see button.tsx).
        className="max-lg:min-h-11"
        disabled={isResetDisabled}
        onClick={resetFilters}
      >
        Reset all filters
      </Button>
      <Button
        type="button"
        disabled={isApplyDisabled}
        onClick={applyFilters}
      >
        Apply
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="flex max-h-[92dvh] flex-col p-0">
          <DrawerTitle className="border-b border-border px-6 py-5 text-xl">
            Filters
          </DrawerTitle>
          {body}
          {footer}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[560px] [&>button]:hidden"
      >
        <div className="flex items-start justify-between gap-3 border-b border-border px-6 py-5">
          <SheetTitle className="text-xl">More Filters</SheetTitle>
          <SheetClose asChild>
            <button
              type="button"
              aria-label="Close"
              className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25"
            >
              <X className="h-4 w-4" />
            </button>
          </SheetClose>
        </div>
        {body}
        {footer}
      </SheetContent>
    </Sheet>
  );
};

export default MoreFiltersDrawer;
